import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Section } from "@/components/Section";
import { useLeadGuard } from "@/components/LeadGuard";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { BUSINESS_HOURS, BUSINESS_HOURS_SPECIFICATION } from "@/content/business-hours";
import { Mail, MapPin, Clock, ArrowUpRight, Linkedin, Phone, MessageCircle } from "lucide-react";
import { NextSteps } from "@/components/NextSteps";
import { buildSeo } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RangpurAreasStrip } from "@/components/RangpurAreasStrip";

export const Route = createFileRoute("/contact")({
  head: () => buildSeo({
    title: "Contact Rahatul Ashiq Tamal — Hire an SEO Expert in Bangladesh",
    description: "Hire Rahatul Ashiq Tamal for SEO consulting, technical audits, local SEO, Shopify SEO, and AEO. Based in Rangpur, Bangladesh. Replies within 1 business day.",
    path: "/contact",
    ogEyebrow: "Contact",
    keywords: "hire SEO expert Bangladesh, SEO consultant contact, freelance SEO Rangpur, SEO audit quote",
    alternates: { bn: "/bn/contact" },
  }),
  component: Contact,
});

const EMPTY = { name: "", email: "", company: "", budget: "Not sure yet", message: "" };

function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const { guardFields, honeypot } = useLeadGuard();

  /** Pre-filled mail draft, kept as a visible fallback route rather than the only path. */
  const mailtoHref = `mailto:${SITE.email}?subject=${encodeURIComponent("New project inquiry")}&body=${encodeURIComponent(
    `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nBudget: ${form.budget}\n\n${form.message}`,
  )}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch("/api/public/newsletter", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          source: "contact-page",
          ...guardFields(),
          meta: {
            name: form.name,
            company: form.company,
            budget: form.budget,
            message: form.message,
          },
        }),
      });
      if (res.ok) {
        setStatus("sent");
        setForm(EMPTY);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <h1 className="sr-only">Contact Rahatul Ashiq Tamal</h1>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "ContactPage",
            "@id": `${SITE.url}/contact#contactpage`,
            url: `${SITE.url}/contact`,
            name: `Contact ${SITE.fullName}`,
            description: `Contact ${SITE.fullName}, SEO expert based in ${SITE.city}, ${SITE.country}.`,
            inLanguage: "en",
            isPartOf: { "@id": `${SITE.url}/#website` },
            mainEntity: { "@id": `${SITE.url}/#person` },
          },
          {
            "@type": "Person",
            "@id": `${SITE.url}/#person`,
            name: SITE.fullName,
            url: SITE.url,
            image: `${SITE.url}/og-image.jpg`,
            jobTitle: "SEO Expert & Digital Marketing Consultant",
            email: SITE.email,
            telephone: SITE.phone,
            address: {
              "@type": "PostalAddress",
              addressLocality: SITE.city,
              addressRegion: SITE.region,
              addressCountry: SITE.countryCode,
            },
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: SITE.phone,
                contactType: "customer support",
                email: SITE.email,
                areaServed: ["BD", "Worldwide"],
                availableLanguage: ["English", "Bengali"],
              },
            ],
            sameAs: Object.values(SITE.social),
          },
          {
            "@type": "ProfessionalService",
            "@id": `${SITE.url}/#business`,
            name: SITE.name,
            url: SITE.url,
            image: `${SITE.url}/og-image.jpg`,
            email: SITE.email,
            telephone: SITE.phone,
            priceRange: "$$",
            address: {
              "@type": "PostalAddress",
              addressLocality: SITE.city,
              addressRegion: SITE.region,
              addressCountry: SITE.countryCode,
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: SITE.geo.lat,
              longitude: SITE.geo.lng,
            },
            areaServed: ["Rangpur", "Bangladesh", "Worldwide"],
            openingHoursSpecification: BUSINESS_HOURS_SPECIFICATION,
            hasMap: "https://www.google.com/maps/place/Rangpur,+Bangladesh/@25.7439,89.2752,13z",
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: SITE.phone,
                contactType: "sales",
                email: SITE.email,
                areaServed: ["BD", "Worldwide"],
                availableLanguage: ["English", "Bengali"],
              },
            ],
            founder: { "@id": `${SITE.url}/#person` },
            sameAs: Object.values(SITE.social),
          },
        ],
      }} />

      <Section eyebrow="Contact" title={<>Let's <span className="text-gradient">talk.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Tell me what you're working on and what you're trying to fix. I'll
          reply within one business day with an honest take on whether I can
          help — and if not, who you should talk to instead.
        </p>
      </Section>

      <div className="container-x grid lg:grid-cols-5 gap-10 pb-32">
        <form onSubmit={submit} className="relative lg:col-span-3 rounded-3xl border border-border bg-card p-8 lg:p-12 space-y-6">
          {honeypot}
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Your name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input" />
            </Field>
            <Field label="Email" required>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Company / website">
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="input" />
            </Field>
            <Field label="Budget range">
              <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input">
                <option>Not sure yet</option>
                <option>Under ৳50,000 / $500</option>
                <option>৳50,000–৳2,00,000 / $500–$2,000</option>
                <option>$2,000–$5,000</option>
                <option>$5,000+</option>
              </select>
            </Field>
          </div>
          <Field label="What are you working on?" required>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="input resize-none" placeholder="Goal, current site, timeline, anything else useful…" />
          </Field>
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={status === "sending"}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Send inquiry"} <ArrowUpRight size={16} />
            </button>
            <a href={mailtoHref} className="text-sm text-muted-foreground underline hover:text-foreground">
              Or email me directly
            </a>
          </div>
          {status === "sent" ? (
            <p role="status" className="text-sm text-primary">
              Got it — your inquiry is in. I reply within one business day.
            </p>
          ) : status === "error" ? (
            <p role="alert" className="text-sm text-destructive">
              That didn't go through. Please{" "}
              <a href={mailtoHref} className="underline">email me directly</a> and I'll pick it up.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Sent straight to my inbox — no mail app needed.
            </p>
          )}
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <div className="rounded-3xl border border-border p-8">
            <h3 className="font-display text-xl">Direct</h3>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3"><Mail size={16} className="text-primary mt-1" /><a href={`mailto:${SITE.email}`} className="hover:text-primary">{SITE.email}</a></li>
              <li className="flex items-start gap-3"><Phone size={16} className="text-primary mt-1" /><a href={`tel:${SITE.phone}`} className="hover:text-primary">{SITE.phone}</a></li>
              <li className="flex items-start gap-3"><MessageCircle size={16} className="text-primary mt-1" /><a href={`https://wa.me/${SITE.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener" className="hover:text-primary">Chat on WhatsApp</a></li>
              <li className="flex items-start gap-3"><MapPin size={16} className="text-primary mt-1" /><span>{SITE.city}, {SITE.region}<br />{SITE.country}</span></li>
              <li className="flex items-start gap-3"><Clock size={16} className="text-primary mt-1" /><span>Replies within 1 business day<br /><span className="text-muted-foreground">{BUSINESS_HOURS.en}</span></span></li>
            </ul>
          </div>
          <div className="rounded-3xl border border-border p-8">
            <h3 className="font-display text-xl">Elsewhere</h3>
            <ul className="mt-6 space-y-3 text-sm">
              <li><a href={SITE.social.linkedin} className="inline-flex items-center gap-2 hover:text-primary"><Linkedin size={14} /> LinkedIn</a></li>
            </ul>
          </div>
          <div className="rounded-3xl border border-border p-8 bg-card">
            <h3 className="font-display text-xl">Currently open for</h3>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li>· Monthly SEO, AEO and GEO retainers (limited slots — ask for the next start date)</li>
              <li>· Technical SEO audits</li>
              <li>· AEO / GEO strategy sprints</li>
            </ul>
          </div>
          <div className="rounded-3xl border border-border overflow-hidden">
            <iframe
              title={`Map of ${SITE.city}, ${SITE.country}`}
              src={`https://www.google.com/maps?q=${SITE.geo.lat},${SITE.geo.lng}&z=12&output=embed`}
              width="100%"
              height="240"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0, display: "block" }}
              allowFullScreen
            />
          </div>
        </aside>
      </div>

      <NextSteps />

      <RangpurAreasStrip
        eyebrow="Local · Rangpur"
        title={<>Prefer to meet <span className="text-gradient">in your area?</span></>}
        description="I work with clients across every Rangpur neighborhood. Browse the area pages below or message me with your location for a hyperlocal SEO conversation."
      />

      <style>{`
        .input { width: 100%; background: var(--input); border: 1px solid var(--border); color: var(--foreground); border-radius: 12px; padding: 14px 16px; font-size: 14px; outline: none; transition: border-color .2s; }
        .input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px oklch(0.88 0.19 130 / 0.15); }
      `}</style>
    </>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
        {label}{required && <span className="text-primary"> *</span>}
      </span>
      {children}
    </label>
  );
}
